import { useEffect, useMemo, useRef, useState } from "react";
import { Form as FinalForm, useForm, useFormState as useFinalFormState } from "react-final-form";

import Grid from "@material-ui/core/Grid";
import FormField from "./FormField";
import { Button } from "./Button";
import { getCookieValue } from "../utils/getCookieValue";
import { useInertiaState } from "./Layout";
import { Trans } from "../utils/i18n";
import { Alert } from "@material-ui/lab";
import { Box } from "@material-ui/core";
import styled from "styled-components";
import {Step, StepLabel, Stepper, useMediaQuery} from "@mui/material";
import {useFormState, useFormStateDispatch} from "../FormComponents/FormState";
import {trackConversion} from "../utils/analytics";
import {extractHouseNumber} from "../utils/extractHouseNumber";
import {usePage} from "@inertiajs/inertia-react";
import { isFunction, last } from "lodash";
import arrayMutators from 'final-form-arrays'
import { ARRAY_ERROR } from "final-form";
import { useEvent, useEventBus } from "../stores/EventBusProvider";
import { useQueryClient } from "@tanstack/react-query";
import { useShoppingCartIsInitialized, useShoppingCartProducts, useShoppingCartStore } from "../stores/ShoppingCartStore";
import useDeepCompareEffect from "use-deep-compare-effect";
import { useTransportProducts } from "../hooks/useTransportProducts";

const createConditionFilter = (values, state) => (fieldOrGroup, step) => {
    if (!fieldOrGroup.condition) {
        return true;
    }

    const paths = fieldOrGroup.condition.field
        .replace(/\[(.*)\]/gm, ".$1")
        .split(".");

    let value = paths.reduce((values, path) => values[path], values);

    if (fieldOrGroup.condition.compare === "!=") {
        if (value === undefined) {
            return null !== fieldOrGroup.condition.value;
        }
        return value !== fieldOrGroup.condition.value;
    }

    return value === fieldOrGroup.condition.value;
};

const StepperWrapper = styled.div``;

const zodValidate =
  (schema) =>
  (values) => {
    const res = schema.safeParse(values);
    if (!res.success) {
      return flatternZodError(res.error);
    }

    return {};
  };

function flatternZodError(err) {
  return err.errors.reduce(
    (prev, e) => {
        const errorKey = e.path.join(".");
        const errors = {
            ...prev,
        };

        // Important info regarding React Final Form Validation:
        // When we use arrays in the form (e. g. via fieldArray) and we want
        // to validate the array itself (e. g. minimum length), there are some
        // quirks in how React Final Form handles this.
        // See these issues and the linked one for more information:
        // https://github.com/final-form/react-final-form/issues/382
        // https://github.com/final-form/react-final-form-arrays/issues/83#issuecomment-497400564
        // To sum it up, if return an error on the array itself, Final Form will crash and throw
        // and exception. It expects us to use the ARRAY_ERROR key as below.
        if (e.type === "array" || e.expected === "array") {
            errors[errorKey] = [];
            errors[errorKey][ARRAY_ERROR] = e.message
            return errors;
        };

        errors[errorKey] = e.message;
        return errors;
    }, {}
  );
}

function FormPage({
    requesttypes = [],
    orderEntryConfig,
    endpointUrl,
    SuccessPage,
    defaultValues = {},
    disableShopSyncronizer
}) {
    const [formState, setFormState] = useState(undefined);
    const [response, setResponse] = useState(undefined);
    const { companies } = useInertiaState();
    const orderEntryState = useFormState();
    const shoppingCartStore = useShoppingCartStore();
    const isShoppingCartInitialized = useShoppingCartIsInitialized();
    const orderEntryDispatch = useFormStateDispatch();
    const { send } = useEventBus();
    const setActiveStep = (value) => orderEntryDispatch({ type: "setActiveStep", value });
    const { activeStep } = orderEntryState;
    const topRef = useRef();
    const isDesktop = useMediaQuery("(min-width:730px)");
    const pageProps = usePage().props;
    const { currentCountry, countries, user } = pageProps;
    const queryClient = useQueryClient();

    const onSubmit = async (data) => {
        if (orderEntryConfig.prepareSubmitData) {
            data = orderEntryConfig.prepareSubmitData(data, orderEntryState);
        }

        if(orderEntryConfig?.isMultiOrder) {
            Promise.all(data.products?.map((product) => {
                return fetch(endpointUrl, {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        "X-XSRF-TOKEN": getCookieValue("XSRF-TOKEN"),
                        "X-Requested-With": "XMLHttpRequest",
                    },
                    body: JSON.stringify({
                        ...data,
                        requesttype: product.requesttype,
                        serialnumber: product.serialnumber,
                        company_id:
                            data.company_id
                            || orderEntryState?.serialNumber?.order?.company_id
                            || orderEntryState?.verificationRequest?.data?.company_id
                            || orderEntryState?.serialNumber?.openOrders?.[0]?.company_id,
                        serviceProductId: orderEntryState?.serialNumber?.serviceProduct?.id,
                        phone: data.phone !== "+49" ? data.phone : undefined,
                        orderdetail_id: orderEntryState.terminalId?.orderdetail_id,
                        metadata: {
                            orderEntryState,
                            formValues: data,
                        },
                        products: [product],
                        send_empty_box: product.send_empty_box,
                        fileIds: product.fileIds,
                        orderattributes: product.orderattributes,
                        customer_reference: product.customer_reference,
                    }),
                })
                    ?.then((res) => res.json())
                    ?.then((res) => {
                        if (res.success) {
                            return res;
                        }
                        return {
                            ...res,
                            product,
                        }
                    });
            }))
                .then((responses) => {
                    setResponse(responses);
                    setFormState("success");
                })
                .catch(() => {
                    setFormState("error");
                });
            return;
        }

        setFormState("loading");
        await fetch(endpointUrl, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "X-XSRF-TOKEN": getCookieValue("XSRF-TOKEN"),
                "X-Requested-With": "XMLHttpRequest",
            },
            body: JSON.stringify({
                ...data,
                company_id:
                    data.company_id
                    || orderEntryState?.serialNumber?.order?.company_id
                    || orderEntryState?.verificationRequest?.data?.company_id
                    || orderEntryState?.serialNumber?.openOrders?.[0]?.company_id,
                serviceProductId: orderEntryState?.serialNumber?.serviceProduct?.id,
                product: orderEntryState?.serialNumber?.product?.id || data.product,
                fileIds: orderEntryState.files
                    ? orderEntryState.files.map(
                          (item) => item.serverResponse.id
                      )
                    : undefined,
                phone: data.phone !== "+49" ? data.phone : undefined,
                orderdetail_id: orderEntryState.terminalId?.orderdetail_id,
                metadata: {
                    orderEntryState,
                    formValues: data,
                }
            }),
        })
            .then((res) => res.json())
            .then((response) => {
                setResponse(response);
                if (response.success && response.checkout_url) {
                    window.location.href = response.checkout_url;
                    return;
                }
                if (response.success) {
                    setFormState("success");
                    return;
                }
                setFormState("error");
            })
            .catch(() => {
                setFormState("error");
            });
    };

    const street = last(user?.company?.addresses ?? [])?.street ?? undefined;
    const houseNumber = street ? extractHouseNumber(street) : undefined;
    const streetWithoutHouseNumber = street ? street.replace(houseNumber, '').trim() : undefined;
    const initialState = {
        company_id: companies?.length === 1 ? companies[0].id : undefined,
        name: last(user?.company?.addresses ?? [])?.name ?? undefined,
        email: last(user?.company?.addresses ?? [])?.email ?? undefined,
        phone: last(user?.company?.addresses ?? [])?.phone ?? undefined,
        street: streetWithoutHouseNumber,
        housenumber: houseNumber,
        zip: last(user?.company?.addresses ?? [])?.zip ?? undefined,
        city: last(user?.company?.addresses ?? [])?.city ?? undefined,
        country_id: last(user?.company?.addresses ?? [])?.country?.id ?? countries?.find(item => item.code === currentCountry)?.id,
        invoice_country_id: countries?.find(item => item.code === currentCountry)?.id,
        ...(defaultValues ?? {}),
    };

    const steps = orderEntryConfig?.groups?.filter((group) => !!group.heading);
    const activeGroup = orderEntryConfig?.groups?.find((item, index) => index === activeStep.index);
    const isLastGroup = activeStep.index === steps.length - 1;
    // TODO: If we are not in the last groups, we also want to validate all previous groups
    const validate = isLastGroup && orderEntryConfig?.schema ? zodValidate(orderEntryConfig.schema) : !isLastGroup && activeGroup?.schema ? zodValidate(activeGroup.schema) : undefined;

    useEffect(() => {
        // If we have already set an active step, e. g. by initializing through the order metadata, we don't want to change it
        if (activeStep && activeStep.index !== undefined) {
            return;
        }

        const steps = orderEntryConfig?.groups?.filter(
            (group) => !!group.heading
        );

        setActiveStep({
            index: 0,
            id: steps?.[0]?.id,
        });
    }, [activeStep]);

    if (formState === "success") {
        return <SuccessPage response={response} />;
    }

    // We need to wait until the shopping cart is initialized before rendering the form.
    // Before rendering the initialState needs to be set correctly (in our case, the products).
    if (!isShoppingCartInitialized) return null;

    return (
        <FinalForm
            initialValues={initialState}
            // Otherwise the form would re-render and reset some local states if we use
            // deeply nested objects such as products in the initialValues.
            // This is not optimal, but for now this is the easiest way to make it work with RFF.
            initialValuesEqual={() => true}
            mutators={{
                addAddress: ([address], state, { changeValue }) => {
                    state.fields.city?.change(address?.city);
                    state.fields.email?.change(address?.email);
                    state.fields.name?.change(address?.name);
                    state.fields.address2?.change(address?.name_2);
                    state.fields.street?.change(address?.street);
                    state.fields.housenumber?.change(address?.housenumber);
                    state.fields.street2?.change(address?.street2);
                    state.fields.zip?.change(address?.zip);
                    state.fields.phone?.change(address?.phone);
                    state.fields.email?.change(address?.email);
                    state.fields.country_id?.change(address?.country_id);
                },
                setAddress: ([address], state, { changeValue }) => {
                    changeValue(state, "city", (value) => address?.city);
                    changeValue(state, "email", (value) => address?.email);
                    changeValue(state, "name", (value) => address?.name);
                    changeValue(state, "name_2", (value) => address?.name_2);
                    changeValue(state, "street", (value) => address?.street);
                    changeValue(state, "housenumber", (value) => address?.housenumber);
                    changeValue(state, "street2", (value) => address?.street2);
                    changeValue(state, "phone", (value) => address?.phone);
                    changeValue(state, "zip", (value) => address?.zip);
                    changeValue(state, "country_id", (value) => address?.country_id);
                },
                addCompany: ([company_id], state, { changeValue }) => {
                    changeValue(state, "company_id", (value) => company_id);
                },
                addCoverageId: ([coverage_id], state, { changeValue }) => {
                    changeValue(state, "coverage_id", (value) => coverage_id);
                },
                setAddressId: ([addressId], state, { changeValue }) => {
                    changeValue(state, "address", (value) => addressId);
                },
                setSerialnumber: ([serialnumber], state, { changeValue }) => {
                    changeValue(state, "serialnumber", (value) => serialnumber);
                },
                ...arrayMutators,
            }}
            onSubmit={onSubmit}
            validate={validate}
        >
            {({ handleSubmit, submitting, values, form, errors, invalid, touched }) => {
                const conditionFilter = useMemo(
                    () => createConditionFilter(values),
                    [values]
                );

                let fieldSets = 1;
                let preFix = "";

                const activeGroup = orderEntryConfig.groups
                    .filter((group) => !!group.heading)
                    .find((item, index) => index === activeStep.index);

                const nextStep = () => {
                    activeGroup?.onNextButtonClick?.({ dispatch: orderEntryDispatch, state: orderEntryState, form, send, values, queryClient, shoppingCartStore, pageProps });

                    if(invalid) {
                        form.submit();
                        return;
                    }

                    const nextStep = steps[activeStep.index + 1];
                    setActiveStep({
                        index: activeStep.index + 1,
                        id: nextStep?.id,
                    });
                    document
                        .querySelector("#formidtop")
                        ?.scrollIntoView({
                            behavior: "smooth",
                            block: "start",
                            inline: "nearest",
                        });
                    trackConversion({
                        action: 'OrderEntryNextStep:'+(activeStep.index + 1),
                    });
                };

                return (
                    <form onSubmit={handleSubmit} id={"formidtop"}>
                        { !disableShopSyncronizer && <ShoppingCartSynchronizer /> }

                        {Object.keys(orderEntryConfig.on ?? {}).map(event => (
                            <Listener key={event} event={event} callback={orderEntryConfig.on[event]} />
                        ))}

                        {orderEntryState.orderControllerConfig?.enableStepper &&
                            activeStep?.index !== undefined && activeGroup?.showSteps !== false && (
                                <>
                                    <StepperWrapper ref={topRef}>
                                        <Stepper
                                            alternativeLabel={!isDesktop}
                                            style={{
                                                padding: "5px 15px",
                                                backgroundColor: "#E0EEFF",
                                                borderRadius: 5,
                                            }}
                                            connector={
                                                isDesktop ? undefined : null
                                            }
                                        >
                                            {orderEntryConfig.groups
                                                .filter(
                                                    (group) =>
                                                        !!group.heading
                                                )
                                                .map((group, index) => {
                                                    if (
                                                        group.hideFromStepper
                                                    ) {
                                                        return null;
                                                    }
                                                    return (
                                                        <Step
                                                            onClick={() => {
                                                                if (
                                                                    index <
                                                                    activeStep?.index
                                                                ) {
                                                                    setActiveStep(
                                                                        {
                                                                            index,
                                                                            id: group.id,
                                                                        }
                                                                    );
                                                                }
                                                            }}
                                                            key={index}
                                                            active={
                                                                activeStep.index ===
                                                                index
                                                            }
                                                            completed={
                                                                activeStep.index >
                                                                index
                                                            }
                                                        >
                                                            <StepLabel
                                                                StepIconProps={{
                                                                    sx: {
                                                                        color: activeStep.index === index ? '#2A5B97' : 'white',
                                                                    }
                                                                }}
                                                            >
                                                                {typeof group.heading ===
                                                                "string" ? (
                                                                    <span
                                                                        style={{
                                                                            fontSize: 14,
                                                                        }}
                                                                        dangerouslySetInnerHTML={{
                                                                            __html: (isDesktop || activeStep.index === index) ? group.heading : '',
                                                                        }}
                                                                    ></span>
                                                                ) : (
                                                                    <span
                                                                        style={{
                                                                            fontSize: 14,
                                                                        }}
                                                                    >
                                                                        {
                                                                            (isDesktop || activeStep.index === index) ? group.heading : ''
                                                                        }
                                                                    </span>
                                                                )}
                                                            </StepLabel>
                                                        </Step>
                                                    );
                                                })}
                                        </Stepper>
                                    </StepperWrapper>
                                </>
                            )}

                        <div key="groups" className={orderEntryConfig.groupsClassName}>
                            {orderEntryConfig.groups.map((groupBase, index) => {
                                const group = isFunction(groupBase) ? groupBase({ dispatch: orderEntryDispatch, state: orderEntryState, form, send, values, queryClient, shoppingCartStore, pageProps }) : groupBase;

                                const fields = group.fields.map(
                                    (fieldBase, index) => {
                                        const field = isFunction(fieldBase) ? fieldBase({ dispatch: orderEntryDispatch, state: orderEntryState, form, send, values, queryClient, shoppingCartStore, pageProps }) : fieldBase;

                                        const size =
                                            orderEntryConfig.sizes[
                                                field.size ? field.size : 'full'
                                            ];

                                        return (
                                            (field.conditionFunction
                                                ? field.conditionFunction(
                                                      values,
                                                      orderEntryState,
                                                      currentCountry,
                                                  )
                                                : true) &&
                                            conditionFilter(field) && (
                                                <Grid
                                                    key={index}
                                                    item
                                                    xs={12}
                                                    sm={size}
                                                >
                                                    <FormField
                                                        disabled={field.disabledFunction ? field.disabledFunction(values) : false}
                                                        field={field}
                                                        preFix={preFix}
                                                        submitting={submitting}
                                                        values={values}
                                                        form={form}
                                                        mutators={form.mutators}
                                                        requesttypes={
                                                            requesttypes
                                                        }
                                                        onClick={() => {
                                                            if (
                                                                field.action ===
                                                                "nextStep"
                                                            ) {
                                                                nextStep();
                                                            }
                                                            if (field.onClick) {
                                                                field.onClick(values, orderEntryState, orderEntryDispatch);
                                                            }
                                                        }}
                                                        nextStep={nextStep}
                                                    />
                                                </Grid>
                                            )
                                        );
                                    }
                                );
                                return (
                                    (group.conditionFunction
                                        ? group.conditionFunction(
                                              values,
                                              orderEntryState,
                                              currentCountry,
                                          )
                                        : true) &&
                                    (orderEntryState.orderControllerConfig
                                        ?.enableStepper
                                        ? (activeStep?.id === group.id || !group.id)
                                        : true) &&
                                    conditionFilter(group) && (
                                        <div key={index}>
                                            {!orderEntryState
                                                .orderControllerConfig
                                                ?.enableStepper && (
                                                <div>
                                                    <div>
                                                        {group.heading && (
                                                            <h3>
                                                                {typeof group.heading ===
                                                                "string" ? (
                                                                    <span
                                                                        style={{
                                                                            fontSize: 14,
                                                                        }}
                                                                        dangerouslySetInnerHTML={{
                                                                            __html: group.heading,
                                                                        }}
                                                                    ></span>
                                                                ) : (
                                                                    <span
                                                                        style={{
                                                                            fontSize: 14,
                                                                        }}
                                                                    >
                                                                        {
                                                                            group.heading
                                                                        }
                                                                    </span>
                                                                )}
                                                            </h3>
                                                        )}
                                                        {group.description && (
                                                            <p>
                                                                {
                                                                    group.description
                                                                }
                                                            </p>
                                                        )}
                                                    </div>
                                                </div>
                                            )}
                                            <Grid container spacing={3}>
                                                {fields}
                                            </Grid>

                                            {orderEntryConfig.groups.length >
                                                index + 1 && (index === 0 ? requesttypes.length > 1 : true) &&
                                                !orderEntryState
                                                    .orderControllerConfig
                                                    ?.enableStepper && <hr style={{ marginTop: 30, marginBottom: 30 }}/>}
                                        </div>
                                    )
                                );
                            })}
                        </div>
                        <div
                            key="actions"
                            style={{
                                marginTop: 30,
                                marginBottom: 15,
                                display: "flex",
                            }}
                        >
                            <Box
                                display="flex"
                                justifyContent="space-between"
                                flex={1}
                            >
                                {orderEntryState.orderControllerConfig
                                    ?.enableStepper && (
                                    <>
                                        {activeStep?.index > 0 && (
                                            <Button
                                                type={"button"}
                                                disabled={submitting}
                                                field={{
                                                    color: "secondary",
                                                    text: activeGroup?.prevButtonText ||  (
                                                        <Trans i18nKey="orderEntry.back">
                                                            Back
                                                        </Trans>
                                                    ),
                                                }}
                                                loading={
                                                    formState === "loading"
                                                }
                                                onClick={() => {
                                                    if (activeGroup?.onPrevButtonClick) {
                                                        activeGroup?.onPrevButtonClick?.({ dispatch: orderEntryDispatch, state: orderEntryState, form, send, values, queryClient, shoppingCartStore, pageProps });
                                                        return;
                                                    }

                                                    const prevStep =
                                                        steps[
                                                            activeStep.index - 1
                                                        ];
                                                    setActiveStep({
                                                        index:
                                                            activeStep.index -
                                                            1,
                                                        id: prevStep?.id,
                                                    });

                                                    trackConversion({
                                                        action: 'OrderEntryPreviousStep:'+(activeStep.index - 1),
                                                    });
                                                }}
                                            />
                                        )}
                                        {
                                            steps[activeStep.index]?.noNextButton !== true &&
                                            !!steps[activeStep.index + 1] &&
                                            (steps[activeStep.index + 1]
                                                .conditionFunction
                                                ? steps[
                                                      activeStep.index + 1
                                                  ].conditionFunction(
                                                      values,
                                                      orderEntryState,
                                                      currentCountry,
                                                  )
                                                : true) && (
                                                <Box marginLeft="auto">
                                                    <Button
                                                        type={"button"}
                                                        disabled={submitting}
                                                        field={{
                                                            color: "secondary",
                                                            text: activeGroup?.nextButtonText || (
                                                                <Trans i18nKey="orderEntry.next">
                                                                    Next
                                                                </Trans>
                                                            ),
                                                        }}
                                                        loading={
                                                            formState ===
                                                            "loading"
                                                        }
                                                        onClick={nextStep}
                                                        id={'order-next-button'}
                                                    />
                                                </Box>
                                            )}
                                    </>
                                )}
                            </Box>
                            {(orderEntryState.orderControllerConfig
                                ?.enableStepper
                                ? activeStep.index === steps.length - 1
                                : true) && (
                                <>
                                    {orderEntryConfig.actions &&
                                        orderEntryConfig.actions.map(
                                            (group, index) => {
                                                const fields = group.fields.map(
                                                    (field, index) => {
                                                        return (
                                                            (field.conditionFunction
                                                                ? field.conditionFunction(
                                                                      values,
                                                                      orderEntryState,
                                                                  )
                                                                : true) &&
                                                            conditionFilter(
                                                                field
                                                            ) && (
                                                                <Button
                                                                    type={
                                                                        field.action ===
                                                                        "next"
                                                                            ? "submit"
                                                                            : "button"
                                                                    }
                                                                    disabled={invalid || submitting}
                                                                    onClick={field?.onClick}
                                                                    field={
                                                                        field
                                                                    }
                                                                    loading={
                                                                        field.action ===
                                                                            "next" &&
                                                                        formState ===
                                                                            "loading"
                                                                    }
                                                                    key={index}
                                                                    id={
                                                                        field.id
                                                                    }
                                                                />
                                                            )
                                                        );
                                                    }
                                                );
                                                return (
                                                    conditionFilter(group) &&
                                                    fields
                                                );
                                            }
                                        )}
                                </>
                            )}

                            <br />
                        </div>

                        {response?.message && (
                            <Alert severity="warning">
                                {response?.message}
                            </Alert>
                        )}

                        {response?.errors &&
                            Object.keys(response?.errors).map(function (
                                key,
                                index
                            ) {
                                return (
                                    <Alert key={index} severity="error">
                                        {response?.errors[key]}
                                    </Alert>
                                );
                            })}
                    </form>
                );
            }}

        </FinalForm>
    );
}

function Listener({ event, callback = () => {}}) {
    const form = useForm();
    const state = useFormState();
    const dispatch = useFormStateDispatch();
    const { send } = useEventBus();
    useEvent(event, (payload) => callback(payload, { form, state, dispatch, send }));

    return null;
}

/**
 * This component is responsible for synchronizing the shopping cart with the form forth and back.
 * NOTE: Such synchronization logic is often a source of bugs. It seems to work so far but we should
 * be very careful when changing this code.
 * Another approach for the future could be: Make the Form State the single source of truth and make
 * it available globally and put it into the localStorage completely.
 * We should then just compute the necessary values from this state and only care about saving from
 * the form to the localStorage and initializing the form at the beginning from the localStorage.
 */
function ShoppingCartSynchronizer() {
    const form = useForm();
    const state = useFinalFormState();
    const shoppingCartStore = useShoppingCartStore();
    const products = state.values?.products ?? [];
    const filteredProducts = products.filter((product) => product?.amount > 0);
    const { data: transportProductsData } = useTransportProducts({
        products: filteredProducts.map((product) => ({
            id: product.id,
            amount: Number(product.amount ?? 1),
        })),
    });
    const transportProductId = state.values?.transport_product_id;
    const transportProduct = transportProductsData?.transport?.find(
        (product) => product.id == transportProductId
    );

    /**
     * Change products from form -> update cart
     */
    useDeepCompareEffect(() => {
        shoppingCartStore.send({
            type: "setProducts",
            payload: {
                products: filteredProducts,
            },
        });
    }, [filteredProducts])

    /**
     * Change transport product from form -> update cart
     */
    useEffect(() => {
        if (transportProduct) {
            shoppingCartStore.send({
                type: "setTransportProduct",
                payload: transportProduct,
            });
        }
    }, [transportProduct]);

    /**
     * Remove from cart -> update form
     */
    useEffect(() => {
        const subscription = shoppingCartStore.on("productRemovedFromCart", ({ id }) => {
            form.change("products", products.map(product => product.id == id ? { ...product, amount: 0} : product));
        });

        return subscription.unsubscribe;
    }, [shoppingCartStore, form, products])

    /**
     * Update quantity from cart -> update form
     */
    useEffect(() => {
        const subscription = shoppingCartStore.on("productUpdatedFromCart", ({ id, data }) => {
            form.change("products", products.map(product => product.id == id ? { ...product, ...data} : product));
        });

        return subscription.unsubscribe;
    }, [shoppingCartStore, form, products])

    /**
     * Update transport product from cart -> update form
     */
    useEffect(() => {
        const subscription = shoppingCartStore.on("transportProductUpdatedFromCart", (transportProduct) => {
            if (transportProduct){
                form.change("transport_product_id", transportProduct?.id);
            }
        });

        return subscription.unsubscribe;
    }, [shoppingCartStore, form, products])

    return null;
}

export default FormPage;
